import React, { useMemo, useCallback, useContext, useState, useEffect } from 'react';
import { Alert, List } from 'antd';
import { BrandContext } from "../../common/BrandContext";
import Button from "../../common/Button";
import VideoPlayer from '../../media/VideoPlayer';
import WidgetPlayer from '../../media/WidgetPlayer';
import ScorcherPlayer from '../../media/ScorcherPlayer';
import DesmosPlayer from '../../media/DesmosPlayer';

const MediaActivities = ({ step, stepActivities }) => {
    const brandConfig = useContext(BrandContext);
    const [selectedActivity, setSelectedActivity] = useState(null);

    const mediaTypeMap = useMemo(() => ({
        VIDEO: {
            name: brandConfig.videosName.toLowerCase(),
            player: VideoPlayer
        },
        WIDGET: {
            name: brandConfig.widgetsName.toLowerCase(),
            player: WidgetPlayer
        },
        SCORCHER: {
            name: brandConfig.scorcherName.toLowerCase(),
            player: ScorcherPlayer
        },
        DESMOS: {
            name: brandConfig.desmosName.toLowerCase(),
            player: DesmosPlayer
        },
        WORKSHEET: {
            name: brandConfig.worksheetsName.toLowerCase(),
            external: true
        },
        TEXTBOOK: {
            name: brandConfig.textbooksName.toLowerCase(),
            external: true
        }
    }), [brandConfig]);

    const mediaConfig = mediaTypeMap[step?.mediaType] || { name: 'media' };
    const mediaName = mediaConfig.name;

    const handleActivityClick = useCallback((stepActivity) => {
        if (!stepActivity) return;

        if (mediaConfig.external) {
            if (stepActivity.mediaUrl) {
                window.open(stepActivity.mediaUrl, '_blank', 'noopener,noreferrer');
            }
        } else {
            setSelectedActivity(stepActivity);
        }
    }, [mediaConfig]);

    const renderPlayer = () => {
        if (!selectedActivity || !mediaConfig.player) return null;

        if (step?.mediaType === 'SCORCHER') {
            return (
                <ScorcherPlayer
                    stepId={step.stepId}
                    scorcherType="PATHWAY"
                />
            );
        }

        if (!step?.conceptId) {
            return (
                <Alert
                    type="error"
                    showIcon
                    message="Configuration Error"
                    description={`Unable to load ${mediaName}. The Concept ID is missing for this step.`}
                    style={{ marginTop: 16 }}
                />
            );
        }

        const PlayerComponent = mediaConfig.player;
        return (
            <PlayerComponent
                mediaId={selectedActivity.mediaId}
                conceptId={step.conceptId}
            />
        );
    };

    useEffect(() => {
        if (selectedActivity) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [selectedActivity]);

    if (!step || !stepActivities || stepActivities.length === 0) {
        return (
            <Alert
                showIcon={true}
                type="warning"
                role="alert"
                aria-live="polite"
                message={`There are no ${mediaName} in this ${brandConfig.conceptName.toLowerCase()}`}
            />
        );
    }

    return (
        <div role="region" aria-label={`${mediaName} viewer`}>
            {selectedActivity ? (
                <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                        <Button
                            type="text"
                            onClick={() => setSelectedActivity(null)}
                            aria-label={`Back to ${mediaName} list`}
                        >
                            ← Back to {mediaName}
                        </Button>
                    </div>
                    {renderPlayer()}
                </>
            ) : (
                <List
                    itemLayout="vertical"
                    dataSource={stepActivities}
                    renderItem={(stepActivity) => (
                        <List.Item key={stepActivity.mediaId}>
                            <List.Item.Meta
                                avatar={
                                    <span
                                        role="img"
                                        aria-label={`${step.mediaType?.toLowerCase() || 'media'} icon`}
                                    >
                                        <svg
                                            width="64px"
                                            height="64px"
                                            viewBox="0 0 128 128"
                                            aria-hidden="true"
                                            focusable="false"
                                        >
                                            <use xlinkHref={`#${step.icon}-icon`} />
                                        </svg>
                                    </span>
                                }
                                title={<span>{stepActivity.mediaName}</span>}
                                description={
                                    <>
                                        <p>{stepActivity.mediaDescription}</p>
                                        <Button
                                            type="primary"
                                            onClick={() => handleActivityClick(stepActivity)}
                                            aria-label={`${
                                                mediaConfig.external ? 'Open' : 'Play'
                                            } ${stepActivity.mediaName}`}
                                        >
                                            {mediaConfig.external ? 'Open' : 'Play'}
                                        </Button>
                                    </>
                                }
                            />
                        </List.Item>
                    )}
                />
            )}
        </div>
    );
};

export default MediaActivities;